import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Trash2, ShoppingBag, Loader2, Tag, X } from "lucide-react";
import { api, assetUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

export default function CartPage() {
  const { items, remove, subtotal } = useCart();
  const [loading, setLoading] = useState(false);
  const [promo, setPromo] = useState("");
  const [applied, setApplied] = useState(null);
  const [applying, setApplying] = useState(false);

  // Clear an applied promo if the cart contents/total change.
  useEffect(() => { setApplied(null); }, [subtotal]);

  const applyPromo = async () => {
    if (!promo.trim()) return;
    setApplying(true);
    try {
      const { data } = await api.post("/checkout/validate-discount", {
        code: promo.trim(),
        items: items.map((i) => ({ product_key: i.product_key, quantity: i.quantity })),
      });
      setApplied(data);
      toast.success(`Promo applied — ${data.label}`);
    } catch (e) {
      setApplied(null);
      toast.error(e.response?.data?.detail || "Invalid promo code");
    } finally {
      setApplying(false);
    }
  };

  const removePromo = () => { setApplied(null); setPromo(""); };

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        items: items.map((i) => ({ product_key: i.product_key, quantity: i.quantity })),
        origin_url: window.location.origin,
        discount_code: applied?.code || null,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  if (items.length === 0) return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center">
      <ShoppingBag className="w-14 h-14 text-slate-300 mx-auto mb-4" />
      <h1 className="font-serif-display text-3xl font-semibold text-[#0A0F1D]">Your cart is empty</h1>
      <Link to="/shop" className="inline-block mt-6 bg-[#3B1F6E] text-white font-semibold px-7 py-3 rounded-full">Browse the Shop</Link>
    </div>
  );

  const total = applied ? applied.new_total : subtotal;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <h1 className="font-serif-display text-4xl font-bold text-[#0A0F1D] tracking-tight mb-8">Your Cart</h1>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {items.map((i) => (
            <div key={i.product_key} data-testid={`cartpage-item-${i.product_key}`} className="flex gap-4 items-center bg-white rounded-2xl border border-slate-200 p-4">
              <div className="w-16 h-20 rounded-lg bg-slate-100 overflow-hidden shrink-0">{i.cover_url && <img src={assetUrl(i.cover_url)} alt={i.title} className="w-full h-full object-cover" />}</div>
              <div className="flex-1 min-w-0">
                <p className="font-serif-display text-lg font-semibold text-[#0A0F1D]">{i.title}</p>
                <p className="text-sm text-slate-500">Qty {i.quantity}</p>
              </div>
              <span className="font-semibold text-[#2E1065]">${(i.price * i.quantity).toFixed(2)}</span>
              <button data-testid={`cartpage-remove-${i.product_key}`} onClick={() => remove(i.product_key)} className="p-2 text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-6 h-fit">
          <h3 className="font-semibold text-[#0A0F1D] mb-4">Order Summary</h3>

          {/* Promo code */}
          <div className="mb-4">
            {!applied ? (
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Tag className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    data-testid="promo-input"
                    value={promo}
                    onChange={(e) => setPromo(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === "Enter" && applyPromo()}
                    placeholder="Promo code"
                    className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-sm uppercase tracking-wide focus:outline-none focus:ring-2 focus:ring-[#3B1F6E]/30 focus:border-[#3B1F6E]"
                  />
                </div>
                <button data-testid="promo-apply" onClick={applyPromo} disabled={applying || !promo.trim()}
                  className="h-10 px-4 rounded-lg bg-[#3B1F6E] text-white text-sm font-semibold disabled:opacity-50">
                  {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                </button>
              </div>
            ) : (
              <div data-testid="promo-applied" className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                <span className="text-sm font-semibold text-green-700 flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> {applied.code} — {applied.label}</span>
                <button data-testid="promo-remove" onClick={removePromo} className="p-1 text-green-700 hover:text-green-900"><X className="w-4 h-4" /></button>
              </div>
            )}
          </div>

          <div className="flex justify-between text-sm mb-2"><span className="text-slate-500">Subtotal</span><span className="font-semibold" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span></div>
          {applied && (
            <div className="flex justify-between text-sm mb-2 text-green-700"><span>Discount ({applied.label})</span><span className="font-semibold" data-testid="cart-discount">−${applied.discount_amount.toFixed(2)}</span></div>
          )}
          <div className="flex justify-between text-base mb-4 pt-2 border-t border-slate-100"><span className="font-semibold text-[#0A0F1D]">Total</span><span className="font-bold text-[#2E1065]" data-testid="cart-total">${total.toFixed(2)}</span></div>
          <button data-testid="checkout-button" disabled={loading} onClick={checkout}
            className="w-full h-12 bg-[#D97706] hover:bg-[#B45309] disabled:opacity-60 text-white rounded-xl font-semibold flex items-center justify-center gap-2">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting...</> : "Checkout Securely"}
          </button>
        </div>
      </div>
    </div>
  );
}
